import { useGetAllSchedules } from "@features/ax/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { useToast } from "@shared/hooks/use-toast";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Calendar, Plus } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface PMSchedule {
  scheduleId: string;
  scheduleName: string;
  type: string;
  frequency: string;
  runs: string;
  assets: string;
  nextDueDate: string;
  lastCompleted: string;
  progress: string;
  status: string;
}

interface PMSchedulesProps {
  onCreateSchedule?: () => void;
  onManageSchedules?: () => void;
  onViewDetails?: (schedule: PMSchedule) => void;
  onEditSchedule?: (schedule: PMSchedule) => void;
  onRunNow?: (schedule: PMSchedule) => void;
  onPauseSchedule?: (schedule: PMSchedule) => void;
  onDeleteSchedule?: (schedule: PMSchedule) => void;
}

const getTypeColor = (type: string): string => {
  switch (type) {
    case "preventive":
      return "bg-blue-100 text-blue-800";
    case "inspection":
      return "bg-green-100 text-green-800";
    case "calibration":
      return "bg-purple-100 text-purple-800";
    case "cleaning":
      return "bg-cyan-100 text-cyan-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";
    case "overdue":
      return "bg-red-100 text-red-800";
    case "due soon":
      return "bg-yellow-100 text-yellow-800";
    case "paused":
      return "bg-gray-100 text-gray-800";
    case "completed":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const ScheduleLanding: React.FC<PMSchedulesProps> = ({
  onCreateSchedule,
  onManageSchedules,
  onViewDetails,
  onEditSchedule,
  onRunNow,
}) => {
  const { toast } = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<any>({
    status: "all",
    frequency_type: "all",
    page: 1,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const PAGE_SIZE = 5;
  const scheduleSource = "5";
  const { data: schedulesData } = useGetAllSchedules(
    id,
    scheduleSource,
    currentPage,
    PAGE_SIZE,
    searchTerm,
    filters.status !== "all" ? filters.status : undefined,
    filters.frequency_type !== "all" ? filters.frequency_type : undefined
  );

  useEffect(() => {
    logEvent("schedules_tab_viewed");
  }, []);

  const columns: TableColumn<any>[] = [
    {
      key: "scheduleNo",
      header: "Schedule ID",
      type: "text",
      headerTooltip:
        "Unique identifier for each preventive maintenance or inspection schedule.",
    },
    {
      key: "scheduleName",
      header: "Schedule Name",
      type: "text",
      headerTooltip:
        "Descriptive title of the schedule to help identify its purpose and scope.",
    },
    {
      key: "scheduleTypeDisplay",
      header: "Type",
      headerTooltip:
        "Indicates whether the schedule is for Preventive Maintenance (PM) or Inspection (INSP).",
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getTypeColor(value),
      },
    },
    {
      key: "frequencyTypeDisplay",
      header: "Frequency",
      type: "text",
      headerTooltip:
        "The recurrence interval of the schedule (e.g., Monthly, Quarterly, Annually, or usage-based like Every 500 Hrs).",
    },
    {
      key: "runCount",
      header: "Runs",
      type: "custom",
      headerTooltip:
        "Total number of times this schedule has been executed since creation.",
      render: (value: number) => (
        <span className="ml-6 font-medium">{value || 0}</span>
      ),
    },
    {
      key: "totalEntities",
      header: "Entities",
      headerTooltip: "Number of entities covered under this schedule.",
      render: (value: number) => (
        <span className="ml-6 font-medium">{value || 0}</span>
      ),
    },
    {
      key: "nextExecutionDate",
      header: "Next Due Date",
      type: "text",
      headerTooltip:
        "The planned date for the next scheduled task to be carried out.",
    },
    {
      key: "lastExecutionDate",
      header: "Last Completed",
      type: "text",
      headerTooltip:
        "The date when the last run of this schedule was successfully completed.",
    },
    {
      key: "progressPercentage",
      header: "Progress",
      type: "text",
      headerTooltip:
        "Shows the number of tasks completed versus total assigned tasks in the current cycle.",
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      headerTooltip: "Current state of the schedule (Active or Inactive).",
      render: (value: string) => (
        <Badge className={getStatusColor(value)}>
          {value ? "Active" : "Inactive"}
        </Badge>
      ),
    },
  ];

  const actions: TableAction<any>[] = useMemo(
    () => [
      {
        label: "View Details",
        onClick: (item) => {
          navigate(`/ax/service-order/schedule/view/${item.id}`);
        },
      },
    ],
    [navigate]
  );

  const totalItems = schedulesData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  // Add same filter handlers as reference component
  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    const defaultFilters: any = {
      status: "all",
      frequency_type: "all",
      page: 1,
    };
    setFilters(defaultFilters);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCreateSchedule = () => {
    navigate("/ax/service-order/schedule/create");
  };

  const handleManageSchedules = () => {
    if (onManageSchedules) {
      onManageSchedules();
    } else {
      toast({
        title: "Manage Schedules",
        description: "Opening schedule management interface",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Calendar className="h-5 w-5 text-indigo-600" />
          </div>
          <PageHeader
            title="PM Schedules"
            subtitle="Preventive maintenance and inspection schedules for this facility"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleCreateSchedule}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Schedule
          </Button>
        </div>
      </div>

      {/* GenericTable with same search, filter, and pagination logic as reference */}
      <GenericTable
        data={schedulesData?.results}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search schedules by name, ID..."
        searchParamName="query"
        searchDebounceMs={300}
        searchValue={searchTerm}
        onSearch={handleSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No schedules found matching your criteria"
      />
    </div>
  );
};
